<script lang="ts">
    import {
        type IAssetGenerationController,
        QualityLevel,
    } from "../../controller/IAssetGenerationController";
    import { ConfigurationManager } from "../../ConfigurationManager";
    import {
        AssetGenerationBundle,
        AssetGenerationProgress,
    } from "../../model";
    import { fade } from "svelte/transition";
    import jQuery from "jquery";
    import { Slot, SLOT_TYPES } from "../../model/Slot";
    import SlotInfoCard from "./SlotInfoCard.svelte";
    import { Booking, BookingAsset } from "../../model/Booking";
    import {
        DefaultErrorHandler,
        getMessagingService,
        MESSAGE_TYPE,
        type MessagingService,
    } from "../../utility/ContextHelper.svelte";
    import { calculateCardColsForSlotAssetCandidates } from "../../utility/CardUtility.svelte";
    import AssetCandidateCard from "../components/AssetCandidateCard.svelte";
    import { getAuthInfo } from "../../utility/AccountUtility";
    import Dropzone from "dropzone";
    import type { UploadAssetPost200Response } from "../../gen/assetgeneration";

    /**
     * The slot for which assets are being generated or uploaded.
     * For simple bookings this is the main slot; for complex bookings
     * this is one of the subslots.
     */
    export let slot: Slot;

    /**
     * The booking request to attach selected assets to.
     * Use bind:bookingRequest in the parent.
     */
    export let bookingRequest: Booking;

    // --------------- File Upload ---------------

    enum FILE_TYPE {
        Model3D,
        Picture,
    }

    interface AssetType {
        ext: string[];
        mediaType: string;
        type: FILE_TYPE;
    }

    let fileTypeWhitelist: FILE_TYPE[] = [];

    const contentTypeMap: Map<string, AssetType> = new Map([
        [
            "glb",
            {
                ext: ["glb"],
                mediaType: "model/gltf-binary",
                type: FILE_TYPE.Model3D,
            },
        ],
        [
            "png",
            {
                ext: ["png"],
                mediaType: "image/png",
                type: FILE_TYPE.Picture,
            },
        ],
        [
            "jpg",
            {
                ext: ["jpg", "jpeg"],
                mediaType: "image/jpeg",
                type: FILE_TYPE.Picture,
            },
        ],
    ]);

    // Derive file type whitelist from slot type whenever slot changes
    $: if (slot) {
        fileTypeWhitelist =
            slot.type === SLOT_TYPES.OBJECT_3D
                ? [FILE_TYPE.Model3D]
                : [FILE_TYPE.Picture];
    }

    function isValidFileType(
        name: string,
        whitelistedAssetTypes: FILE_TYPE[],
    ): boolean {
        let ext = name.substring(name.lastIndexOf(".") + 1);
        let ct: AssetType = contentTypeMap.get(ext);
        if (!ct) {
            return undefined;
        }
        return whitelistedAssetTypes.indexOf(ct.type) >= 0;
    }

    let msgSrvc: MessagingService = getMessagingService();

    function initDropzone(node: HTMLElement) {
        function deriveContentMediaType(file) {
            let name = file.name as string;
            let ext = name.substring(name.lastIndexOf(".") + 1);
            let ct: AssetType = contentTypeMap.get(ext);
            if (!ct) {
                return undefined;
            }
            return ct.mediaType;
        }

        const dz = new Dropzone(node, {
            headers: {
                "Access-Control-Request-Headers": "authorization,content-type",
            },
            paramName: "file",
            maxFilesize: 50,
            accept: function (file, done) {
                if (!isValidFileType(file.name as string, fileTypeWhitelist)) {
                    done(
                        "Invalid file type (extension). Acceptible types: " +
                            Array.from(contentTypeMap.values())
                                .filter((v: AssetType) => {
                                    return (
                                        fileTypeWhitelist.indexOf(v.type) >= 0
                                    );
                                })
                                .map((va: AssetType) => {
                                    return va.ext;
                                })
                                .join(", "),
                    );
                } else {
                    if (!file.type.match(/image.*/)) {
                        return done();
                    }

                    const reader = new FileReader();
                    reader.onload = function (event) {
                        const img = new Image();
                        img.onload = function () {
                            const ratio = img.width / img.height;
                            const targetRatio =
                                slot.dimensions[0] / slot.dimensions[1];
                            const tolerance = 0.01;

                            if (Math.abs(ratio - targetRatio) > tolerance) {
                                done(
                                    `Image must have a ${slot.dimensions[0]} / ${slot.dimensions[1]} aspect ratio.`,
                                );
                            } else {
                                //@ts-ignore
                                file.width = img.width;
                                //@ts-ignore
                                file.height = img.height;
                                done();
                            }
                        };
                        img.src = event.target.result as string;
                    };
                    reader.readAsDataURL(file);
                }
            },
            url:
                ConfigurationManager.getInstance().assetGenerationController.getBaseUrl() +
                "/upload/asset",
            method: "post",
            sending: function (file, xhr, formData) {
                const _send = xhr.send;
                xhr.send = function () {
                    _send.call(xhr, file);
                };
            },
            init: function () {
                this.on("sending", function (file, xhr) {
                    xhr.setRequestHeader(
                        "Content-Type",
                        deriveContentMediaType(file),
                    );
                    xhr.setRequestHeader(
                        "Authorization",
                        `Bearer ${getAuthInfo().getToken()}`,
                    );
                });

                this.on(
                    "success",
                    function (file, response: UploadAssetPost200Response) {
                        console.log("Asset URL:", response.url);
                        console.log("New File ID:", response.id);

                        file["serverId"] = response.id;
                        file["assetUrl"] = response.url;

                        dz.removeFile(file);
                        msgSrvc.BoradcastMessage(
                            "Successfully uploaded file " + file.name,
                            MESSAGE_TYPE.SUCCESS,
                        );

                        let newAssetCandidate = new BookingAsset();
                        newAssetCandidate.id = response.id;
                        newAssetCandidate.url = response.url;
                        newAssetCandidate.selected = false;
                        newAssetCandidate.refSlot = slot.id;

                        bookingRequest.assets.push(newAssetCandidate);
                        bookingRequest = bookingRequest;
                    },
                );

                this.on("error", function (file, errorMessage, xhr) {
                    DefaultErrorHandler(
                        "Could not upload file: " + errorMessage,
                    );
                    console.error("Upload failed:", errorMessage);
                    dz.removeFile(file);
                });
            },
        });
        return {
            destroy() {
                dz.destroy();
            },
        };
    }

    // --------------- Asset Generation ---------------

    let assetGenerationController: IAssetGenerationController =
        ConfigurationManager.getInstance().assetGenerationController;

    let msgService: MessagingService = getMessagingService();

    /**
     * The active inner tab for asset generation/upload selection
     */
    let activeInnerTab: string = "generate";

    /**
     * The currently active job for asset generation
     */
    let activeJob: AssetGenerationBundle = undefined;

    /**
     * A merged list of all jobs from saved and active for display
     */
    let allJobs: Map<string, AssetGenerationProgress> = new Map();

    /**
     * The number of card grid columns to occupy per generated asset
     */
    let cardGridCols: number = 3;

    $: if (slot) {
        cardGridCols = calculateCardColsForSlotAssetCandidates(slot);
    }

    /**
     * The interval in milliseconds for polling asset generation status
     */
    const updateStatusDelay = 2000;

    function contains<T>(arr: T[], fn: (a: T) => boolean): boolean {
        for (let obj of arr) {
            if (fn(obj)) {
                return true;
            }
        }
        return false;
    }

    function updateAllJobsMap(jobProgress: AssetGenerationBundle) {
        let result = new Map();
        activeJob = jobProgress;
        if (activeJob) {
            for (let job of activeJob.subJobs) {
                if (!result.has(job.jobId)) {
                    result.set(job.jobId, job);
                }
                // Once the URL is available, move the candidate into bookingRequest.assets
                // as unselected so it appears in the summary regardless of slot page.
                if (job.assetUrl && !bookingRequest.assets.some(
                    (a: BookingAsset) => a.id === job.jobId && a.refSlot === slot.id
                )) {
                    const asset = new BookingAsset();
                    asset.id = job.jobId;
                    asset.url = job.assetUrl;
                    asset.selected = false;
                    asset.refSlot = slot.id;
                    bookingRequest.assets.push(asset);
                }
            }
        }
        allJobs = result;
        bookingRequest = bookingRequest;
    }

    function updateStatus() {
        if (!activeJob) {
            return;
        }
        assetGenerationController
            .getGroupStatus(activeJob)
            .then((val: AssetGenerationBundle) => {
                updateAllJobsMap(val);

                if (activeJob.getProgressPercent() < 1) {
                    setTimeout(updateStatus, updateStatusDelay);
                } else {
                    updateAllJobsMap(val);
                    jQuery("#btnAssetGenerate").prop("disabled", false);
                    if (
                        activeJob.subJobs.filter(
                            (job: AssetGenerationProgress): boolean => {
                                return !job.assetUrl;
                            },
                        ).length > 0
                    ) {
                        setTimeout(updateStatus, updateStatusDelay);
                    } else {
                        msgService.BoradcastMessage(
                            "Finished generating assets",
                            MESSAGE_TYPE.SUCCESS,
                        );
                    }
                }
            }).catch((error) => {
                msgService.BroadCastError("Could not create asset: " + error);
            });
    }

    function generateClicked(event: Event) {
        jQuery(event.target).prop("disabled", true);
        let prompt = jQuery("#textprompt").val();
        event.preventDefault();

        if (prompt == "Magic" || prompt == "magic") {
            let exampleBundle: AssetGenerationBundle =
                new AssetGenerationBundle();
            let exampleAsset: AssetGenerationProgress =
                new AssetGenerationProgress();

            exampleAsset.jobId = "magic-" + slot.id;
            exampleAsset.progress = 100;
            exampleAsset.total = 100;
            exampleAsset.assetUrl =
                "http://localhost:8072/asset/e3f0148f-d733-4e97-8acf-b53d264e8f44";

            exampleBundle.subJobs.push(exampleAsset);
            updateAllJobsMap(exampleBundle);
            jQuery(event.target).prop("disabled", false);
            return false;
        }
        assetGenerationController
            .generateMultipleImagesFromPrompt(
                prompt as string,
                undefined,
                slot.dimensions,
                2,
                QualityLevel.FAST,
                slot.transparency ?? false,
            )
            .then((val: AssetGenerationBundle) => {
                updateAllJobsMap(val);
                setTimeout(updateStatus, updateStatusDelay);
            })
            .catch((err) => {
                jQuery(event.target).prop("disabled", false);
            });
        return false;
    }

    function keepImageCheckboxClicked(assetId: string) {
        const asset = bookingRequest.assets.find(
            (a: BookingAsset) => a.id === assetId && a.refSlot === slot.id
        );
        if (asset) {
            asset.selected = !asset.selected;
            bookingRequest = bookingRequest;
        }
    }
</script>

<h2 class="text-center mb-0">Generate Assets</h2>
<p class="text-secondary text-center">
    Upload or generate a set of assets to request for booking
</p>

{#if slot && slot.type === "complex"}
    <p class="text-secondary text-center fw-semibold">
        Sub-slot: <span class="text-body">{slot.name}</span>
    </p>
{/if}

<div class="row">
    <div class="col-12 col-xl-6 ps-0">
        {#if slot}
            <SlotInfoCard
                horizontal={window.screen.width > 1600}
                {slot}
            ></SlotInfoCard>
        {/if}
    </div>

    <div class="col-12 col-xl-6 pe-0">
        <div class="card h-100">
            <ul
                class="nav nav-tabs nav-fill"
                id="asset-upload-generation-selection"
                role="tablist"
            >
                <li class="nav-item" role="presentation">
                    <button
                        class="nav-link {activeInnerTab === 'generate'
                            ? 'active'
                            : ''}"
                        id="generate-tab"
                        type="button"
                        role="tab"
                        aria-controls="generate"
                        aria-selected={activeInnerTab === "generate"}
                        on:click={() => (activeInnerTab = "generate")}
                        >Generate</button
                    >
                </li>
                <li class="nav-item" role="presentation">
                    <button
                        class="nav-link {activeInnerTab === 'upload'
                            ? 'active'
                            : ''}"
                        id="upload-tab"
                        type="button"
                        role="tab"
                        aria-controls="upload"
                        aria-selected={activeInnerTab === "upload"}
                        on:click={() => (activeInnerTab = "upload")}
                        >Upload</button
                    >
                </li>
            </ul>
            <div class="tab-content px-4 h-100" id="myTabContent">
                <div
                    class="tab-pane h-100 {activeInnerTab === 'generate'
                        ? 'show active'
                        : 'hide'}"
                    id="generate"
                    role="tabpanel"
                    aria-labelledby="generate-tab"
                >
                    <div class="d-flex flex-column h-100">
                        <label class="form-label" for="textprompt">Prompt</label>
                        <textarea
                            name="prompt"
                            id="textprompt"
                            class="form-control w-100 mb-2 fs-3 flex-grow-1"
                            >{slot ? slot.prompt : ""}</textarea
                        >
                        <button
                            id="btnAssetGenerate"
                            class="btn btn-primary align-self-start my-2"
                            on:click={generateClicked}
                            >Generate</button
                        >
                    </div>
                </div>
                <div
                    class="tab-pane {activeInnerTab === 'upload'
                        ? 'show active'
                        : 'hide'}"
                    id="upload"
                    role="tabpanel"
                    aria-labelledby="upload-tab"
                >
                    <div class="dropzone text-center px-4 py-6 h-100" use:initDropzone>
                        <div class="dz-message d-flex flex-column">
                            <img
                                class="avatar avatar-xxl mb-3 flex-grow-1 align-self-center"
                                src="../assets/images/illustrations/upload-illustration.svg"
                                alt="..."
                            />
                            <h5 class="mb-4">Drag and drop your file here</h5>
                            <p class="mb-4">or</p>
                            <span class="btn btn-sm btn-gray-300">Browse files</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>

<h2 class="text-center mt-6 mb-0">Your Asset Candidates</h2>
<p class="text-secondary text-center mb-6">
    Request a booking with a list of suitable assets
</p>
{#if allJobs.size + bookingRequest.assets.filter(a => a.refSlot === slot.id).length > 0}
    <div class="my-3" in:fade>
        <div class="row">
            {#each bookingRequest.assets.filter(a => a.refSlot === slot.id) as asset}
                <AssetCandidateCard
                    assetId={asset.id}
                    assetUrl={asset.url}
                    {cardGridCols}
                    checked={asset.selected}
                    keepImageCheckboxCallback={keepImageCheckboxClicked}
                    {slot}
                ></AssetCandidateCard>
            {/each}
            {#each Array.from(allJobs.values()) as job, index (job)}
                {#if !bookingRequest.assets.some((a: BookingAsset) => a.id === job.jobId && a.refSlot === slot.id)}
                    <AssetCandidateCard
                        assetId={job.jobId}
                        assetUrl={job.assetUrl}
                        {cardGridCols}
                        checked={false}
                        {job}
                        keepImageCheckboxCallback={keepImageCheckboxClicked}
                        {slot}
                    ></AssetCandidateCard>
                {/if}
            {/each}
        </div>
        <!-- / .row -->
    </div>
{/if}
